import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ReportAccessGuard } from '../../components/ReportAccessGuard';
import {
  getReportModuleCapabilities,
  REPORT_ACCESS_CODES,
} from '../../config/reportCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { getDepartments } from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import { runReportPreview } from '../../services/reportService';
import type {
  MonthlyAttendanceReportFilters,
  MonthlyAttendanceReportRow,
} from '../../types/monthlyAttendanceReport';
import {
  createDefaultMonthlyAttendanceFilters,
  filterMonthlyAttendanceReportRows,
  formatAttendancePercentage,
  formatMonthlyAttendanceShortLabel,
  mapMonthlyAttendanceReportRows,
  monthlyAttendanceApiFilters,
  shiftMonthlyAttendancePeriod,
  summarizeMonthlyAttendanceReportRows,
  validateMonthlyAttendancePeriod,
} from '../../utils/monthlyAttendanceReport';
import { reportErrorMessage } from '../../utils/reportErrors';

type SummaryMetric = {
  key: 'workingDays' | 'presentDays' | 'leaveDays' | 'absentDays';
  label: string;
  value: number;
  color: string;
  labelColor: string;
};

type LocationOption = { id: number; label: string };

type Props = NativeStackScreenProps<
  RootStackParamList,
  'MonthlyAttendanceReport'
>;

const REPORT_CODE = 'MONTHLY_ATTENDANCE_SUMMARY';
const REPORT_NAME = 'Monthly Attendance';
const PREVIEW_MAX_ROWS = 500;
/** Ignore extra focuses while a preview is in flight or just finished. */
const FOCUS_RELOAD_COOLDOWN_MS = 2000;

const COL_EMPLOYEE = 180;
const COL_METRIC = 72;
const COL_PERCENT = 64;

/** Design accent for Absent Days when not in the shared palette. */
const ABSENT_PURPLE = '#7C3AED';

function FilterIcon({ color }: { color: string }) {
  return (
    <View style={styles.filterIcon} accessibilityElementsHidden>
      <View
        style={[styles.filterLine, styles.filterWide, { backgroundColor: color }]}
      />
      <View
        style={[styles.filterLine, styles.filterMid, { backgroundColor: color }]}
      />
      <View
        style={[
          styles.filterLine,
          styles.filterNarrow,
          { backgroundColor: color },
        ]}
      />
    </View>
  );
}

function ChevronIcon({
  color,
  direction,
}: {
  color: string;
  direction: 'left' | 'right';
}) {
  return (
    <Text style={[styles.chevron, { color }]}>
      {direction === 'left' ? '‹' : '›'}
    </Text>
  );
}

function SearchIcon({ color }: { color: string }) {
  return (
    <View style={styles.searchIcon} accessibilityElementsHidden>
      <View style={[styles.searchCircle, { borderColor: color }]} />
      <View style={[styles.searchHandle, { backgroundColor: color }]} />
    </View>
  );
}

function MonthlyTableRow({
  row,
  palette,
  isLast,
}: {
  row: MonthlyAttendanceReportRow;
  palette: ColorPalette;
  isLast: boolean;
}) {
  const percentLabel = formatAttendancePercentage(row.percentage);
  return (
    <View
      style={[
        styles.bodyRow,
        {
          borderBottomColor: palette.border,
          borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
        },
      ]}
      accessibilityLabel={`${row.employee}, present ${row.presentDays}, absent ${row.absentDays}, leave ${row.leaveDays}, ${percentLabel}`}
    >
      <View style={[styles.employeeCell, { width: COL_EMPLOYEE }]}>
        <ProfileAvatar name={row.employee === '—' ? '?' : row.employee} size={36} />
        <Text
          style={[styles.employeeName, { color: palette.textPrimary }]}
          numberOfLines={2}
        >
          {row.employee}
        </Text>
      </View>
      <Text
        style={[styles.cellText, styles.metricText, { width: COL_METRIC, color: palette.textPrimary }]}
      >
        {row.presentDays}
      </Text>
      <Text
        style={[styles.cellText, styles.metricText, { width: COL_METRIC, color: palette.textPrimary }]}
      >
        {row.absentDays}
      </Text>
      <Text
        style={[styles.cellText, styles.metricText, { width: COL_METRIC, color: palette.textPrimary }]}
      >
        {row.leaveDays}
      </Text>
      <Text
        style={[
          styles.cellText,
          styles.metricText,
          { width: COL_PERCENT, color: palette.textSecondary },
        ]}
      >
        {percentLabel}
      </Text>
    </View>
  );
}

export function MonthlyAttendanceReportScreen({ navigation }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const capabilities = getReportModuleCapabilities(auth.permissions);
  const downloads = useReportDownloads();

  const defaults = useMemo(() => createDefaultMonthlyAttendanceFilters(), []);
  const [draft, setDraft] = useState<MonthlyAttendanceReportFilters>(defaults);
  const [applied, setApplied] =
    useState<MonthlyAttendanceReportFilters>(defaults);

  const [rows, setRows] = useState<MonthlyAttendanceReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [openPicker, setOpenPicker] = useState<'department' | 'location' | null>(
    null,
  );
  const [selectedDepartmentLabel, setSelectedDepartmentLabel] = useState<
    string | null
  >(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(
    null,
  );
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [locationDepartmentNames, setLocationDepartmentNames] = useState<
    string[] | null
  >(null);
  const hasFocused = useRef(false);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const mountedRef = useRef(true);
  const fetchGeneration = useRef(0);
  const inFlightKey = useRef<string | null>(null);
  const lastSuccessAt = useRef(0);

  const canGoBack =
    typeof navigation.canGoBack === 'function' && navigation.canGoBack();

  const themed = useMemo(
    () => ({
      error: { ...typography.body, color: palette.error, marginTop: spacing.sm },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.sm,
      },
      headerCell: {
        ...typography.caption,
        fontWeight: '700' as const,
        color: palette.textMuted,
      },
    }),
    [palette],
  );

  const monthLabel = formatMonthlyAttendanceShortLabel(draft.year, draft.month);

  const fetchReport = useCallback(
    async (filters: MonthlyAttendanceReportFilters, isRefresh = false) => {
      if (!capabilities.canPreview) {
        setError('You do not have permission to preview this report.');
        setLoading(false);
        return;
      }

      const periodError = validateMonthlyAttendancePeriod(
        filters.year,
        filters.month,
      );
      if (periodError) {
        setFilterError(periodError);
        setError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const apiFilters = monthlyAttendanceApiFilters(filters.year, filters.month);
      if (!apiFilters) {
        setFilterError('Unable to resolve the selected month.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const requestKey = `${apiFilters.year}|${apiFilters.month}`;
      if (inFlightKey.current === requestKey) {
        return;
      }

      const currentRequest = ++fetchGeneration.current;
      inFlightKey.current = requestKey;
      setFilterError(null);
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const preview = await runReportPreview({
          reportCode: REPORT_CODE,
          filters: apiFilters,
          maxRows: PREVIEW_MAX_ROWS,
        });
        if (!mountedRef.current || currentRequest !== fetchGeneration.current) {
          return;
        }
        setRows(mapMonthlyAttendanceReportRows(preview.rows));
        setTruncated(preview.truncated);
        setApplied(current =>
          current.year === filters.year &&
          current.month === filters.month &&
          current.department === filters.department
            ? current
            : filters,
        );
        setError(null);
        lastSuccessAt.current = Date.now();
      } catch (err) {
        if (!mountedRef.current || currentRequest !== fetchGeneration.current) {
          return;
        }
        setRows([]);
        setError(
          reportErrorMessage(err, 'Unable to load monthly attendance.'),
        );
      } finally {
        if (currentRequest === fetchGeneration.current) {
          inFlightKey.current = null;
          if (mountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      }
    },
    [capabilities.canPreview],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getLocations({ page: 1, pageSize: 100, filters: { isActive: true } })
      .then(result => {
        if (cancelled) return;
        setLocationOptions(
          result.items.map(item => ({
            id: item.locationId,
            label: item.locationName,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setLocationOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      const isRefresh = hasFocused.current;
      hasFocused.current = true;
      if (
        isRefresh &&
        lastSuccessAt.current > 0 &&
        Date.now() - lastSuccessAt.current < FOCUS_RELOAD_COOLDOWN_MS
      ) {
        return;
      }
      fetchReport(appliedRef.current, isRefresh).catch(() => undefined);
    }, [fetchReport]),
  );

  const departmentOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of rows) {
      if (row.department && row.department !== '—') {
        names.add(row.department);
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const departmentRows = useMemo(() => {
    let next = filterMonthlyAttendanceReportRows(rows, {
      department: applied.department,
    });
    if (locationDepartmentNames) {
      const allowed = new Set(
        locationDepartmentNames.map(name => name.trim().toLowerCase()),
      );
      next = next.filter(
        row =>
          row.department != null &&
          allowed.has(row.department.trim().toLowerCase()),
      );
    }
    return next;
  }, [applied.department, locationDepartmentNames, rows]);

  const visibleRows = useMemo(() => {
    const query = employeeQuery.trim().toLowerCase();
    if (!query) return departmentRows;
    return departmentRows.filter(row =>
      row.employee.toLowerCase().includes(query),
    );
  }, [departmentRows, employeeQuery]);

  const summary = useMemo(
    () => summarizeMonthlyAttendanceReportRows(departmentRows),
    [departmentRows],
  );

  const summaryMetrics = useMemo<SummaryMetric[]>(
    () => [
      {
        key: 'workingDays',
        label: 'Working Days',
        value: summary.workingDays,
        color: palette.success,
        labelColor: palette.error,
      },
      {
        key: 'presentDays',
        label: 'Present Days',
        value: summary.presentDays,
        color: palette.error,
        labelColor: palette.textPrimary,
      },
      {
        key: 'leaveDays',
        label: 'Leave Days',
        value: summary.leaveDays,
        color: palette.warning,
        labelColor: palette.warning,
      },
      {
        key: 'absentDays',
        label: 'LOP Days',
        value: summary.absentDays,
        color: ABSENT_PURPLE,
        labelColor: palette.textPrimary,
      },
    ],
    [palette.error, palette.success, palette.textPrimary, palette.warning, summary],
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.includes(`${REPORT_CODE}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads],
  );

  const updateDraftYear = (value: string) => {
    const trimmed = value.trim();
    const parsed = trimmed === '' ? NaN : Number(trimmed);
    setDraft(current => ({
      ...current,
      year: Number.isInteger(parsed) ? parsed : current.year,
    }));
    setFilterError(null);
    setDownloadError(null);
  };

  const updateDraftMonth = (value: string) => {
    const trimmed = value.trim();
    const parsed = trimmed === '' ? NaN : Number(trimmed);
    setDraft(current => ({
      ...current,
      month: Number.isInteger(parsed) ? parsed : current.month,
    }));
    setFilterError(null);
    setDownloadError(null);
  };

  const applyFilters = () => {
    fetchReport({
      year: draft.year,
      month: draft.month,
      department: draft.department.trim(),
    }).catch(() => undefined);
  };

  const changeMonthBy = (deltaMonths: number) => {
    const nextPeriod = shiftMonthlyAttendancePeriod(
      draft.year,
      draft.month,
      deltaMonths,
    );
    const next = {
      ...draft,
      year: nextPeriod.year,
      month: nextPeriod.month,
    };
    setDraft(next);
    setFilterError(null);
    setDownloadError(null);
    fetchReport(next).catch(() => undefined);
  };

  const selectDepartment = (label: string | null) => {
    setSelectedDepartmentLabel(label);
    setOpenPicker(null);
    const nextDepartment = label ?? '';
    setDraft(current => ({ ...current, department: nextDepartment }));
    setApplied(current =>
      current.department === nextDepartment
        ? current
        : { ...current, department: nextDepartment },
    );
  };

  const selectLocation = async (option: LocationOption | null) => {
    setSelectedLocation(option);
    setOpenPicker(null);
    if (!option) {
      setLocationDepartmentNames(null);
      return;
    }
    try {
      const result = await getDepartments({
        page: 1,
        pageSize: 100,
        filters: { locationId: option.id, isActive: true },
      });
      setLocationDepartmentNames(
        result.items.map(item => item.departmentName).filter(Boolean),
      );
    } catch {
      setLocationDepartmentNames([]);
    }
  };

  const exportReport = async (format: 'CSV' | 'XLSX') => {
    setDownloadError(null);
    const next = {
      year: draft.year,
      month: draft.month,
      department: draft.department.trim(),
    };
    const periodError = validateMonthlyAttendancePeriod(next.year, next.month);
    if (periodError) {
      setFilterError(periodError);
      return;
    }

    const allowed =
      format === 'CSV'
        ? capabilities.canExportCsv
        : capabilities.canExportXlsx;
    if (!allowed) {
      setDownloadError('You do not have permission to export reports.');
      return;
    }
    if (downloads.isDownloadInFlight(REPORT_CODE, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }

    try {
      const result = await downloads.startDownload({
        reportCode: REPORT_CODE,
        reportName: REPORT_NAME,
        format,
        filters: { year: next.year, month: next.month },
        view: {
          columns: [
            { key: 'employee', label: 'Employee' },
            { key: 'presentDays', label: 'Present Days' },
            { key: 'absentDays', label: 'LOP Days' },
            { key: 'leaveDays', label: 'Leave Days' },
            { key: 'percentage', label: 'Attendance %' },
          ],
          rows: visibleRows.map(row => ({
            employee: row.employee,
            presentDays: row.presentDays,
            absentDays: row.absentDays,
            leaveDays: row.leaveDays,
            percentage: formatAttendancePercentage(row.percentage),
          })),
        },
      });
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(reportErrorMessage(err, 'The report could not be saved.'));
    }
  };

  const departmentButtonLabel = selectedDepartmentLabel ?? 'All Departments';
  const locationButtonLabel = selectedLocation?.label ?? 'All Locations';

  return (
    <ReportAccessGuard reportCode={REPORT_ACCESS_CODES.MONTHLY_ATTENDANCE}>
      <>
        <AppScreen
          scroll
          keyboardSafe
          webLayout="wide"
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => fetchReport(applied, true)}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader>
            <View style={styles.headerRow}>
              {canGoBack ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Back"
                  onPress={() => navigation.goBack()}
                  hitSlop={8}
                  style={styles.headerSide}
                >
                  <Text style={[styles.headerGlyph, { color: palette.white }]}>
                    ‹
                  </Text>
                </Pressable>
              ) : (
                <View style={styles.headerSide} />
              )}
              <View style={styles.headerCenter}>
                <Text
                  accessibilityRole="header"
                  numberOfLines={1}
                  style={[styles.headerTitle, { color: palette.white }]}
                >
                  {REPORT_NAME}
                </Text>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Filters"
                accessibilityState={{ expanded: showAdvancedFilters }}
                onPress={() => setShowAdvancedFilters(current => !current)}
                hitSlop={8}
                style={styles.headerSideEnd}
              >
                <FilterIcon color={palette.white} />
              </Pressable>
            </View>
          </ScreenHeader>

          <View style={styles.body}>
            <View
              style={[
                styles.dateBar,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                },
              ]}
            >
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Previous month"
                onPress={() => changeMonthBy(-1)}
                hitSlop={8}
                style={styles.dateNavButton}
              >
                <ChevronIcon color={palette.primary} direction="left" />
              </Pressable>
              <Text
                style={[styles.dateLabel, { color: palette.textPrimary }]}
                numberOfLines={1}
              >
                {monthLabel}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Next month"
                onPress={() => changeMonthBy(1)}
                hitSlop={8}
                style={styles.dateNavButton}
              >
                <ChevronIcon color={palette.primary} direction="right" />
              </Pressable>
            </View>

            {showAdvancedFilters ? (
              <View
                style={[
                  styles.advancedCard,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                    shadowColor: palette.black,
                  },
                ]}
              >
                <AppInput
                  label="Year"
                  value={String(draft.year)}
                  onChangeText={updateDraftYear}
                  keyboardType="number-pad"
                  placeholder="YYYY"
                />
                <AppInput
                  label="Month"
                  value={String(draft.month)}
                  onChangeText={updateDraftMonth}
                  keyboardType="number-pad"
                  placeholder="1–12"
                />
                {filterError ? (
                  <Text accessibilityRole="alert" style={themed.error}>
                    {filterError}
                  </Text>
                ) : null}
                <AppButton title="Apply" onPress={applyFilters} />
              </View>
            ) : null}

            {filterError && !showAdvancedFilters ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {filterError}
              </Text>
            ) : null}
            {downloadError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {downloadError}
              </Text>
            ) : null}

            <View style={styles.summaryRow}>
              {summaryMetrics.map(metric => (
                <View
                  key={metric.key}
                  accessibilityLabel={`${metric.label}: ${metric.value}`}
                  style={[
                    styles.summaryCard,
                    {
                      backgroundColor: palette.surface,
                      borderColor: palette.border,
                      shadowColor: palette.black,
                    },
                  ]}
                >
                  <Text style={[styles.summaryValue, { color: metric.color }]}>
                    {metric.value}
                  </Text>
                  <Text
                    style={[styles.summaryLabel, { color: metric.labelColor }]}
                    numberOfLines={2}
                  >
                    {metric.label}
                  </Text>
                </View>
              ))}
            </View>

            <View style={styles.filterRow}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={departmentButtonLabel}
                accessibilityState={{ expanded: openPicker === 'department' }}
                onPress={() =>
                  setOpenPicker(current =>
                    current === 'department' ? null : 'department',
                  )
                }
                style={[
                  styles.dropdownButton,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Text
                  style={[styles.dropdownLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {departmentButtonLabel}
                </Text>
                <Text style={{ color: palette.textMuted }}>▾</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={locationButtonLabel}
                accessibilityState={{ expanded: openPicker === 'location' }}
                onPress={() =>
                  setOpenPicker(current =>
                    current === 'location' ? null : 'location',
                  )
                }
                style={[
                  styles.dropdownButton,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Text
                  style={[styles.dropdownLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {locationButtonLabel}
                </Text>
                <Text style={{ color: palette.textMuted }}>▾</Text>
              </Pressable>
            </View>

            {openPicker === 'department' ? (
              <View
                style={[
                  styles.pickerPanel,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="All Departments"
                  onPress={() => selectDepartment(null)}
                  style={styles.pickerOption}
                >
                  <Text style={{ color: palette.textPrimary }}>
                    All Departments
                  </Text>
                </Pressable>
                {departmentOptions.map(name => (
                  <Pressable
                    key={name}
                    accessibilityRole="button"
                    accessibilityLabel={name}
                    onPress={() => selectDepartment(name)}
                    style={styles.pickerOption}
                  >
                    <Text style={{ color: palette.textPrimary }}>{name}</Text>
                  </Pressable>
                ))}
              </View>
            ) : null}

            {openPicker === 'location' ? (
              <View
                style={[
                  styles.pickerPanel,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="All Locations"
                  onPress={() => {
                    selectLocation(null).catch(() => undefined);
                  }}
                  style={styles.pickerOption}
                >
                  <Text style={{ color: palette.textPrimary }}>
                    All Locations
                  </Text>
                </Pressable>
                {locationOptions.map(option => (
                  <Pressable
                    key={option.id}
                    accessibilityRole="button"
                    accessibilityLabel={option.label}
                    onPress={() => {
                      selectLocation(option).catch(() => undefined);
                    }}
                    style={styles.pickerOption}
                  >
                    <Text style={{ color: palette.textPrimary }}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            ) : null}

            <View
              style={[
                styles.searchField,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                },
              ]}
            >
              <SearchIcon color={palette.textMuted} />
              <TextInput
                accessibilityLabel="Search employee"
                value={employeeQuery}
                onChangeText={setEmployeeQuery}
                placeholder="Search employee..."
                placeholderTextColor={palette.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                style={[styles.searchInput, { color: palette.textPrimary }]}
              />
            </View>

            {activeProgress ? (
              <ReportDownloadProgressView
                progress={activeProgress}
                canCancel={Boolean(activeProgress)}
                onCancel={() =>
                  activeProgress
                    ? downloads.cancelDownload(activeProgress.downloadId)
                    : undefined
                }
              />
            ) : null}

            {loading && rows.length === 0 ? (
              <AppLoader message="Loading monthly attendance" />
            ) : error && rows.length === 0 ? (
              <ErrorState message={error} onRetry={() => fetchReport(applied)} />
            ) : visibleRows.length === 0 ? (
              <EmptyState
                title="No data available"
                message="No employee attendance rows matched the selected month."
                actionTitle="Retry"
                onAction={() => fetchReport(applied)}
              />
            ) : (
              <View
                style={[
                  styles.tableCard,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                {truncated ? (
                  <Text style={themed.warning}>
                    Showing the first {PREVIEW_MAX_ROWS} rows. Export for the full
                    file.
                  </Text>
                ) : null}
                <ScrollView
                  horizontal
                  accessibilityLabel="Monthly attendance table"
                >
                  <View>
                    <View
                      style={[
                        styles.headerRowTable,
                        { backgroundColor: palette.background },
                      ]}
                    >
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          { width: COL_EMPLOYEE },
                        ]}
                        accessibilityRole="header"
                      >
                        Employee
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          styles.metricText,
                          { width: COL_METRIC },
                        ]}
                        accessibilityRole="header"
                      >
                        Present
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          styles.metricText,
                          { width: COL_METRIC },
                        ]}
                        accessibilityRole="header"
                      >
                        LOP
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          styles.metricText,
                          { width: COL_METRIC },
                        ]}
                        accessibilityRole="header"
                      >
                        Leave
                      </Text>
                      <Text
                        style={[
                          themed.headerCell,
                          styles.headerCellPad,
                          styles.metricText,
                          { width: COL_PERCENT },
                        ]}
                        accessibilityRole="header"
                      >
                        %
                      </Text>
                    </View>
                    {visibleRows.map((row, index) => (
                      <MonthlyTableRow
                        key={row.rowKey}
                        row={row}
                        palette={palette}
                        isLast={index === visibleRows.length - 1}
                      />
                    ))}
                  </View>
                </ScrollView>
              </View>
            )}

            {capabilities.canExportCsv || capabilities.canExportXlsx ? (
              <View style={styles.exportRow}>
                {capabilities.canExportCsv ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Export PDF"
                    accessibilityState={{
                      disabled: Boolean(activeProgress),
                      busy: downloads.isDownloadInFlight(REPORT_CODE, 'CSV'),
                    }}
                    disabled={Boolean(activeProgress)}
                    onPress={() => {
                      exportReport('CSV').catch(() => undefined);
                    }}
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: palette.surface,
                        borderColor: palette.primary,
                        opacity: activeProgress ? 0.45 : 1,
                      },
                    ]}
                  >
                    <Text style={[styles.exportLabel, { color: palette.primary }]}>
                      Export PDF
                    </Text>
                  </Pressable>
                ) : null}
                {capabilities.canExportXlsx ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Export Excel"
                    accessibilityState={{
                      disabled: Boolean(activeProgress),
                      busy: downloads.isDownloadInFlight(REPORT_CODE, 'XLSX'),
                    }}
                    disabled={Boolean(activeProgress)}
                    onPress={() => {
                      exportReport('XLSX').catch(() => undefined);
                    }}
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: palette.surface,
                        borderColor: palette.primary,
                        opacity: activeProgress ? 0.45 : 1,
                      },
                    ]}
                  >
                    <Text style={[styles.exportLabel, { color: palette.primary }]}>
                      Export Excel
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </View>
        </AppScreen>
      </>
    </ReportAccessGuard>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerSide: {
    width: 40,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  headerSideEnd: {
    width: 40,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerGlyph: {
    fontSize: 32,
    lineHeight: 34,
    fontWeight: '300',
  },
  headerTitle: {
    ...typography.sectionTitle,
    textAlign: 'center',
    fontWeight: '700',
  },
  filterIcon: {
    width: 18,
    height: 14,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  filterLine: {
    height: 2,
    borderRadius: 1,
  },
  filterWide: { width: 18 },
  filterMid: { width: 12 },
  filterNarrow: { width: 6 },
  body: { marginTop: spacing.lg, gap: spacing.md },
  dateBar: {
    minHeight: 48,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dateNavButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevron: {
    fontSize: 28,
    lineHeight: 30,
    fontWeight: '600',
  },
  dateLabel: {
    ...typography.body,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
  },
  advancedCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.sm,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  summaryCard: {
    flex: 1,
    minHeight: 84,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: 2,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 2,
  },
  summaryValue: {
    ...typography.screenTitle,
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '700',
  },
  summaryLabel: {
    ...typography.caption,
    fontSize: 10,
    lineHeight: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  filterRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dropdownButton: {
    flex: 1,
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dropdownLabel: {
    ...typography.caption,
    fontWeight: '600',
    flex: 1,
  },
  pickerPanel: {
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
  },
  pickerOption: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  searchField: {
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchIcon: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1.5,
  },
  searchHandle: {
    position: 'absolute',
    width: 6,
    height: 1.5,
    borderRadius: 1,
    right: 1,
    bottom: 2,
    transform: [{ rotate: '45deg' }],
  },
  searchInput: {
    ...typography.body,
    flex: 1,
    paddingVertical: spacing.sm,
  },
  tableCard: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    padding: spacing.sm,
  },
  headerRowTable: {
    flexDirection: 'row',
    borderRadius: 10,
  },
  headerCellPad: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
  },
  bodyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 56,
  },
  employeeCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  employeeName: {
    ...typography.caption,
    fontWeight: '600',
    flexShrink: 1,
  },
  cellText: {
    ...typography.caption,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
  },
  metricText: {
    textAlign: 'center',
    fontWeight: '600',
  },
  exportRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.xl,
  },
  exportButton: {
    flex: 1,
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  exportLabel: {
    ...typography.button,
    fontWeight: '700',
  },
});
